import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Sparkles, Wand2, Save, Check, Globe,
  Search, Image as ImageIcon, Code2, FileJson,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useAdminTheme, tc } from './theme';
import SeoScore from './SeoScore';

// ---------------------------------------------------------------------------
// Google SERP preview — replicates how a result appears in search
// ---------------------------------------------------------------------------
function GooglePreview({ pageSlug, title, description, canonical }) {
  const url = canonical || `https://voipinger.com${pageSlug === 'home' ? '' : '/' + pageSlug}`;
  const displayUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return (
    <div className="rounded-xl bg-white text-slate-900 p-4 border border-white/10">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Google result preview</p>
      <div className="text-xs text-slate-700 mb-0.5">{displayUrl}</div>
      <div className="text-[#1a0dab] text-lg font-medium leading-tight hover:underline cursor-default">
        {title || <span className="text-slate-400">Untitled page</span>}
      </div>
      <div className="text-sm text-slate-700 mt-1 leading-snug">
        {description || <span className="text-slate-400">No meta description set.</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable AI buttons for a SEO text field
// ---------------------------------------------------------------------------
function SeoAiActions({ promptKey, currentText, pageName, primaryKeyword, onApply }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const run = async (action) => {
    setBusy(action);
    setErr(null);
    try {
      const res = await api.post('/api/admin-v2/ai/generate', {
        prompt_key: action === 'generate' ? promptKey : 'improve_text',
        action,
        current_text: currentText || '',
        variables: {
          page_name: pageName,
          primary_keyword: primaryKeyword || pageName,
          target_length: promptKey === 'seo_title' ? '55' : '150',
        },
      });
      onApply(res.data?.output || '');
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
      <button
        type="button"
        disabled={busy != null}
        onClick={() => run('generate')}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 hover:text-white disabled:opacity-50"
      >
        {busy === 'generate' ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
        Generate
      </button>
      <button
        type="button"
        disabled={busy != null}
        onClick={() => run('improve')}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 hover:text-white disabled:opacity-50"
      >
        {busy === 'improve' ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
        Improve
      </button>
      {err && (
        <span className="text-[11px] text-rose-300 flex items-center gap-1">
          <AlertCircle size={11} /> {err}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main SEO editor
// ---------------------------------------------------------------------------
const DEFAULT_SEO = {
  title: '',
  description: '',
  canonical: '',
  keywords: [],
  robots: 'index, follow',
  og_title: '',
  og_description: '',
  og_image: '',
  og_type: 'website',
  twitter_card: 'summary_large_image',
  twitter_title: '',
  twitter_description: '',
  twitter_image: '',
  schema_jsonld: null,
};

export default function AdminV2SeoEditor() {
  const { slug } = useParams();
  const { theme } = useAdminTheme();
  const inputCls = `w-full mt-1 px-3 py-2 rounded-lg outline-none text-sm ${tc.inputBg(theme)}`;
  const inputMonoCls = `w-full mt-1 px-3 py-2 rounded-lg outline-none text-sm font-mono ${tc.inputBg(theme)}`;
  const labelCls = `text-[10px] uppercase tracking-widest font-bold ${tc.textMuted(theme)}`;
  const cardCls = `rounded-2xl p-5 space-y-4 ${tc.cardBg(theme)}`;
  const [seo, setSeo] = useState(null);
  const [original, setOriginal] = useState(null);
  const [pageMeta, setPageMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [jsonText, setJsonText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [seoRes, pageRes] = await Promise.all([
        api.get(`/api/admin-v2/seo/${slug}`),
        api.get(`/api/admin-v2/pages/${slug}`).catch(() => null),
      ]);
      const merged = { ...DEFAULT_SEO, ...(seoRes.data || {}), page_slug: slug };
      setSeo(merged);
      setOriginal(JSON.stringify(merged));
      setJsonText(merged.schema_jsonld ? JSON.stringify(merged.schema_jsonld, null, 2) : '');
      if (pageRes) setPageMeta(pageRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = seo && original && JSON.stringify(seo) !== original;

  const update = (key, value) => {
    setSeo((s) => ({ ...s, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    let schema_jsonld = seo.schema_jsonld;
    if (jsonText.trim()) {
      try {
        schema_jsonld = JSON.parse(jsonText);
      } catch (e) {
        setSaving(false);
        setError(`Schema JSON-LD is not valid JSON: ${e.message}`);
        return;
      }
    } else {
      schema_jsonld = null;
    }
    try {
      const body = { ...seo, schema_jsonld, page_slug: slug };
      const res = await api.put(`/api/admin-v2/seo/${slug}`, body);
      const merged = { ...DEFAULT_SEO, ...res.data, page_slug: slug };
      setSeo(merged);
      setOriginal(JSON.stringify(merged));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const generateSchemaJsonLd = async () => {
    try {
      const res = await api.post('/api/admin-v2/ai/generate', {
        prompt_key: 'schema_jsonld',
        action: 'generate',
        current_text: jsonText || '',
        variables: {
          page_name: pageMeta?.name || slug,
          schema_type: slug === 'home' ? 'Organization' : slug.includes('blog') ? 'BlogPosting' : 'Service',
        },
      });
      if (res.data?.parsed) {
        setJsonText(JSON.stringify(res.data.parsed, null, 2));
        setSeo((s) => ({ ...s, schema_jsonld: res.data.parsed }));
      } else if (res.data?.output) {
        setJsonText(res.data.output);
      }
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const titleLen = (seo?.title || '').length;
  const descLen = (seo?.description || '').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/seo" className={`inline-flex items-center gap-1 text-sm ${tc.textMuted(theme)} hover:${tc.textPrimary(theme)}`}>
          <ArrowLeft size={14} /> SEO Hub
        </Link>
        {seo && (
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="seo-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save SEO'}
          </button>
        )}
      </div>

      {error && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${theme === 'light' ? 'text-rose-700 bg-rose-50 border border-rose-200' : 'text-rose-300 bg-rose-500/10 border border-rose-500/30'}`}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {loading && !error && (
        <div className={`flex items-center gap-2 text-sm ${tc.textMuted(theme)}`}>
          <Loader2 size={16} className="animate-spin" /> Loading SEO metadata…
        </div>
      )}

      {seo && (
        <>
          <div>
            <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>SEO Metadata</p>
            <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>{pageMeta?.name || slug}</h1>
            <p className={`text-xs font-mono mt-1 ${tc.textDim(theme)}`}>{pageMeta?.public_path || `/${slug}`}</p>
          </div>

          {isDirty && (
            <div className={`rounded-xl px-4 py-2 text-xs ${theme === 'light' ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-amber-500/10 border border-amber-500/30 text-amber-300'}`}>
              You have unsaved changes.
            </div>
          )}

          <GooglePreview pageSlug={slug} title={seo.title} description={seo.description} canonical={seo.canonical} />

          {/* LIVE SEO SCORE — recomputes on every edit */}
          <SeoScore seo={seo} page={pageMeta} />

          {/* CORE META */}
          <div className={cardCls}>
            <div className="flex items-center gap-2">
              <Search size={16} className="text-fuchsia-300" />
              <h3 className="font-bold text-white">Core metadata</h3>
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label className={labelCls}>Meta title</label>
                <span className={`text-[10px] ${titleLen > 60 ? 'text-rose-300' : titleLen >= 50 ? 'text-emerald-300' : 'text-slate-500'}`}>
                  {titleLen}/60 (Google truncates &gt; ~60)
                </span>
              </div>
              <input
                type="text"
                value={seo.title || ''}
                onChange={(e) => update('title', e.target.value)}
                className={inputCls}
                placeholder="50-60 char SEO title…"
              />
              <SeoAiActions
                promptKey="seo_title"
                currentText={seo.title}
                pageName={pageMeta?.name || slug}
                primaryKeyword={(seo.keywords || [])[0] || ''}
                onApply={(t) => update('title', t)}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label className={labelCls}>Meta description</label>
                <span className={`text-[10px] ${descLen > 160 ? 'text-rose-300' : descLen >= 140 ? 'text-emerald-300' : 'text-slate-500'}`}>
                  {descLen}/160
                </span>
              </div>
              <textarea
                rows={3}
                value={seo.description || ''}
                onChange={(e) => update('description', e.target.value)}
                className={`${inputCls} resize-y`}
                placeholder="140-160 char meta description…"
              />
              <SeoAiActions
                promptKey="seo_description"
                currentText={seo.description}
                pageName={pageMeta?.name || slug}
                primaryKeyword={(seo.keywords || [])[0] || ''}
                onApply={(t) => update('description', t)}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Canonical URL</label>
                <input
                  type="text"
                  value={seo.canonical || ''}
                  onChange={(e) => update('canonical', e.target.value)}
                  className={inputMonoCls}
                  placeholder={`https://voipinger.com${pageMeta?.public_path || ''}`}
                />
              </div>
              <div>
                <label className={labelCls}>Robots directive</label>
                <select
                  value={seo.robots || 'index, follow'}
                  onChange={(e) => update('robots', e.target.value)}
                  className={inputCls}
                >
                  <option value="index, follow">index, follow</option>
                  <option value="noindex, follow">noindex, follow</option>
                  <option value="index, nofollow">index, nofollow</option>
                  <option value="noindex, nofollow">noindex, nofollow</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Target keywords (comma-separated)</label>
              <input
                type="text"
                value={(seo.keywords || []).join(', ')}
                onChange={(e) =>
                  update(
                    'keywords',
                    e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                  )
                }
                className={inputCls}
                placeholder="business voip, voip uk, cloud phone"
              />
            </div>
          </div>

          {/* OPEN GRAPH */}
          <div className={cardCls}>
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-fuchsia-300" />
              <h3 className="font-bold text-white">Open Graph (Facebook, LinkedIn)</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>og:title</label>
                <input
                  type="text"
                  value={seo.og_title || ''}
                  onChange={(e) => update('og_title', e.target.value)}
                  className={inputCls}
                  placeholder="Defaults to meta title if empty"
                />
              </div>
              <div>
                <label className={labelCls}>og:type</label>
                <select
                  value={seo.og_type || 'website'}
                  onChange={(e) => update('og_type', e.target.value)}
                  className={inputCls}
                >
                  <option value="website">website</option>
                  <option value="article">article</option>
                  <option value="product">product</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>og:description</label>
              <textarea
                rows={2}
                value={seo.og_description || ''}
                onChange={(e) => update('og_description', e.target.value)}
                className={inputCls}
                placeholder="Defaults to meta description if empty"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1">
                <ImageIcon size={11} /> og:image (1200×630 recommended)
              </label>
              <input
                type="text"
                value={seo.og_image || ''}
                onChange={(e) => update('og_image', e.target.value)}
                className={inputMonoCls}
                placeholder="https://voipinger.com/assets/og-image.webp"
              />
            </div>
          </div>

          {/* TWITTER */}
          <div className={cardCls}>
            <div className="flex items-center gap-2">
              <Code2 size={16} className="text-fuchsia-300" />
              <h3 className="font-bold text-white">Twitter / X card</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>card</label>
                <select
                  value={seo.twitter_card || 'summary_large_image'}
                  onChange={(e) => update('twitter_card', e.target.value)}
                  className={inputCls}
                >
                  <option value="summary_large_image">summary_large_image</option>
                  <option value="summary">summary</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>image</label>
                <input
                  type="text"
                  value={seo.twitter_image || ''}
                  onChange={(e) => update('twitter_image', e.target.value)}
                  className={inputMonoCls}
                  placeholder="https://voipinger.com/assets/twitter.webp"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>twitter:title</label>
                <input
                  type="text"
                  value={seo.twitter_title || ''}
                  onChange={(e) => update('twitter_title', e.target.value)}
                  className={inputCls}
                  placeholder="Defaults to meta title"
                />
              </div>
              <div>
                <label className={labelCls}>twitter:description</label>
                <input
                  type="text"
                  value={seo.twitter_description || ''}
                  onChange={(e) => update('twitter_description', e.target.value)}
                  className={inputCls}
                  placeholder="Defaults to meta description"
                />
              </div>
            </div>
          </div>

          {/* SCHEMA JSON-LD */}
          <div className={`rounded-2xl p-5 space-y-3 ${tc.cardBg(theme)}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileJson size={16} className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'} />
                <h3 className={`font-bold ${tc.textPrimary(theme)}`}>Structured data (Schema.org JSON-LD)</h3>
              </div>
              <button
                type="button"
                onClick={generateSchemaJsonLd}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 hover:text-white"
              >
                <Sparkles size={11} /> Generate with AI
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Schema.org JSON-LD helps Google understand the page's structure (Organization, Service, Article, FAQ). Paste valid JSON or generate with AI.
            </p>
            <textarea
              rows={10}
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                setSaved(false);
              }}
              className={`w-full px-3 py-2 rounded-lg outline-none text-xs font-mono resize-y ${tc.inputBg(theme)}`}
              placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "Service",\n  "name": "..."\n}`}
            />
          </div>
        </>
      )}
    </div>
  );
}
